import { useEffect, useRef } from 'react'
import { Torch } from '@/services/TorchesService'
import { ICampaignAssets } from '@/types/generated-contentful'
import { Content, mapContent, MappedContent } from '@/utils/TorchGuildUtil'
import { useSafeAnalytics } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { TorchVideoHero } from '../../organisms/TorchVideoHero'
import { BackButton } from './BackButton'
import { Details } from './Details'
import { DetailsProvider } from './DetailsProvider'
import { ExpressInterestCard, StickyExpressInterestCard } from './ExpressInterestCard'
import { FixedMobileExpressInterestButton } from './FixedMobileExpressInterestButton'
import { SectionTabs } from './SectionTabs'

export interface TorchViewProps {
  torch: Torch
  campaignAssets: ICampaignAssets
  content: Content
  isEmbed?: boolean
}

export const TorchView: React.FC<TorchViewProps> = ({ torch, campaignAssets, content, isEmbed = false }) => {
  const topCtaBtnRef = useRef(null)

  const { track } = useSafeAnalytics()
  const pageContent = mapContent(content) as MappedContent & { content: Content[] }
  const { query } = useSafeRouter()

  const { t } = useTranslate('torches')
  const back = t('torches', 'Torches')

  useEffect(() => {
    track('Land on Torch Page', {
      campaign: query.torch,
    })
  }, [track, query.torch])

  return (
    <DetailsProvider content={pageContent?.content}>
      <div className="min-h-screen bg-core-gray-100 font-whitney">
        <div className="bg-core-gray-950">
          {!isEmbed && <BackButton className="mb-4 ml-2 inline-block lg:ml-4">{back}</BackButton>}
          <div className="mx-auto flex max-w-7xl flex-col xl:flex-row">
            {!isEmbed && (
              <TorchVideoHero campaignAssets={campaignAssets.fields} campaignSlug={String(query.torch)} torch={torch} />
            )}

            <div className="w-full bg-core-gray-100 p-4 pt-8 2lg:hidden">
              <ExpressInterestCard
                className="mx-auto max-w-[640px]"
                ref={topCtaBtnRef}
                campaignAssets={campaignAssets}
                displayDescription
                displayTags
                isEmbed={isEmbed}
              />
            </div>
            {!isEmbed && (
              <div className="hidden items-center xl:flex">
                <ExpressInterestCard
                  className="max-w-[460px] px-6"
                  ref={null}
                  campaignAssets={campaignAssets}
                  theme="dark"
                  isEmbed={isEmbed}
                />
              </div>
            )}
          </div>
        </div>
        <SectionTabs isEmbed={isEmbed} />

        <div className="mx-auto grid max-w-screen-xl grid-cols-3 gap-8 py-10 md:px-10 md:py-12 2lg:px-8">
          <Details className="col-span-3 mx-auto lg:max-w-[816px] 2lg:col-span-2 2lg:max-w-none" />
          <div className="hidden 2lg:block">
            <StickyExpressInterestCard campaignAssets={campaignAssets} isEmbed={isEmbed} />
          </div>
        </div>
        {!isEmbed && <FixedMobileExpressInterestButton campaignSlug={String(query.torch)} topCtaRef={topCtaBtnRef} />}
      </div>
    </DetailsProvider>
  )
}
